// ─────────────────────────────────────────────
//  roomOccupant — WHICH row is the room running right now.
//
//  A room legitimately has more than one assignment row: a morning case and an
//  afternoon case, or a split OB day/night pair. `assignmentForRoom` returns
//  the FIRST match, and Firestore hands rows back in no meaningful order, so
//  every surface that asks "who is in this room" has been answering with an
//  arbitrary one of them.
//
//  ⚠️ THIS IS NOT COSMETIC. `BoardAlertControls` resolves an EMERGENCY's
//  recipients through that same lookup — the room's doctor and CRNA. On a room
//  with a morning row and an afternoon row, an emergency raised at 3 PM could
//  page the CRNA who went home at noon and leave the person actually holding
//  the patient off the broadcast. The break flow, the room card and the room's
//  message channel had the same hole.
//
//  THE RULE, in the order a clinician would answer it:
//    1. A real case whose window contains NOW. That is the room's case.
//    2. Otherwise a standing on-call watch (OB) — the room is covered, but by
//       somebody who is free between cases, so a real case outranks it.
//    3. Otherwise the NEXT case to start today: nobody is in the room yet and
//       the person about to be is the useful answer.
//    4. Otherwise the case that finished most recently.
//    5. Otherwise the first match, exactly as before.
//
//  Called WITHOUT a clock it returns `assignmentForRoom`'s answer unchanged, so
//  callers that want "the row for this room" in a configuration sense (the
//  assignment modal, next-room links) are untouched.
//
//  ⚠️ OVERNIGHT WINDOWS WRAP. A 19:00→07:00 row ends on the following day, so
//  "contains now" cannot be a plain `start <= now < end` — at 23:00 that test
//  is false for the very row that is running. The wrap case is tested.
// ─────────────────────────────────────────────
import { assignmentsForRoom } from "./gridTemplate";
import { hmToMinutes, isStandingOnCall } from "./timeline";

const MINUTES_PER_DAY = 24 * 60;

// A row's [start, end) in minutes, or null when it carries no usable times.
function windowOf(a) {
  const start = hmToMinutes(a?.startTime);
  const end = hmToMinutes(a?.endTime);
  if (start == null || end == null) return null;
  return { start, end };
}

// Does this window hold `nowMin`? An end at or before the start is an overnight
// row (19:00→07:00, or the importer's 07:00→07:00 all-day watch).
function covers(win, nowMin) {
  if (!win) return false;
  if (win.end > win.start) return nowMin >= win.start && nowMin < win.end;
  return nowMin >= win.start || nowMin < win.end;
}

// The row a room is running at `nowMin` (minutes since midnight, local).
export function occupantRowForRoom(roomName, assignments, nowMin = null) {
  const rows = assignmentsForRoom(roomName, assignments);
  if (rows.length <= 1 || nowMin == null || !Number.isFinite(nowMin)) {
    return rows[0] || null;
  }

  const now = ((nowMin % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const watches = rows.filter((a) => isStandingOnCall(a));
  const cases = rows.filter((a) => !isStandingOnCall(a));

  // 1. The case running now.
  const running = cases.find((a) => covers(windowOf(a), now));
  if (running) return running;

  // 2. A standing watch covers the room the rest of the time.
  if (watches.length) return watches[0];

  // 3. The next case to start today — nobody is in there yet, and the person
  //    about to be is what a reader is actually asking about.
  const upcoming = cases
    .map((a) => ({ a, w: windowOf(a) }))
    .filter((x) => x.w && x.w.start > now)
    .sort((x, y) => x.w.start - y.w.start)[0];
  if (upcoming) return upcoming.a;

  // 4. The case that finished most recently.
  const finished = cases
    .map((a) => ({ a, w: windowOf(a) }))
    .filter((x) => x.w)
    .sort((x, y) => y.w.end - x.w.end)[0];
  if (finished) return finished.a;

  // 5. Timeless rows only — the old answer.
  return rows[0] || null;
}

// Minutes since local midnight, for callers that hold a Date rather than the
// board's own `nowMin`.
export const nowMinutes = (date = new Date()) => date.getHours() * 60 + date.getMinutes();
